import { useEffect, useRef, useState, useCallback } from "react";

// Bank-app-style inactivity logout. The timer ticks down based on real user
// input (mouse, keyboard, scroll, touch) — NOT background API polling — so
// the user is signed out when they walk away even if the app is still
// fetching realtime data in the background.
//
// Cross-tab: a BroadcastChannel keeps every open tab in sync so activity in
// one tab doesn't let an idle tab kick the user out from under them.

const ACTIVITY_EVENTS = [
  "mousedown",
  "mousemove",
  "keydown",
  "scroll",
  "touchstart",
  "wheel",
];

// Throttle activity broadcasts. mousemove fires hundreds of times per second;
// resetting timers that often is wasteful and can starve other React work.
const ACTIVITY_THROTTLE_MS = 1000;

const BROADCAST_CHANNEL_NAME = "bus-assignments-activity";

const DEFAULT_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour total
const DEFAULT_WARNING_MS = 5 * 60 * 1000; // 5 min before timeout

const readEnvNumber = (key, fallback) => {
  const raw = Number(process.env[key]);
  return Number.isFinite(raw) && raw > 0 ? raw : fallback;
};

export const useInactivityTimeout = ({
  onTimeout,
  enabled = true,
  timeoutMs = readEnvNumber("REACT_APP_INACTIVITY_TIMEOUT_MS", DEFAULT_TIMEOUT_MS),
  warningMs = readEnvNumber("REACT_APP_INACTIVITY_WARNING_MS", DEFAULT_WARNING_MS),
} = {}) => {
  const [isWarning, setIsWarning] = useState(false);
  const [secondsRemaining, setSecondsRemaining] = useState(0);

  const warningTimerRef = useRef(null);
  const timeoutTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const lastActivityRef = useRef(0);
  const channelRef = useRef(null);

  const clearAllTimers = useCallback(() => {
    if (warningTimerRef.current) {
      clearTimeout(warningTimerRef.current);
      warningTimerRef.current = null;
    }
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current);
      timeoutTimerRef.current = null;
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
      countdownIntervalRef.current = null;
    }
  }, []);

  const startWarningCountdown = useCallback(() => {
    setIsWarning(true);
    setSecondsRemaining(Math.ceil(warningMs / 1000));
    countdownIntervalRef.current = setInterval(() => {
      setSecondsRemaining((s) => Math.max(0, s - 1));
    }, 1000);
  }, [warningMs]);

  const resetTimers = useCallback(() => {
    clearAllTimers();
    setIsWarning(false);

    warningTimerRef.current = setTimeout(() => {
      startWarningCountdown();
      timeoutTimerRef.current = setTimeout(() => {
        onTimeout?.();
      }, warningMs);
    }, timeoutMs - warningMs);
  }, [clearAllTimers, startWarningCountdown, onTimeout, timeoutMs, warningMs]);

  // Called both for local input events and broadcasts from other tabs.
  const recordActivity = useCallback(
    ({ broadcast = false } = {}) => {
      const now = Date.now();
      if (now - lastActivityRef.current < ACTIVITY_THROTTLE_MS) return;
      lastActivityRef.current = now;
      resetTimers();
      if (broadcast && channelRef.current) {
        try {
          channelRef.current.postMessage({ type: "activity", t: now });
        } catch {
          // Channel may have been closed during teardown; ignore.
        }
      }
    },
    [resetTimers]
  );

  // Public — for the warning modal's "Stay signed in" button. Treated as a
  // real activity event so it resets timers AND syncs other tabs.
  const stayActive = useCallback(() => {
    recordActivity({ broadcast: true });
  }, [recordActivity]);

  useEffect(() => {
    if (!enabled) {
      clearAllTimers();
      setIsWarning(false);
      return undefined;
    }

    const handleLocalActivity = () => recordActivity({ broadcast: true });

    if (typeof BroadcastChannel !== "undefined") {
      channelRef.current = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      channelRef.current.onmessage = (event) => {
        if (event?.data?.type === "activity") {
          recordActivity({ broadcast: false });
        }
      };
    }

    ACTIVITY_EVENTS.forEach((eventName) => {
      globalThis.addEventListener(eventName, handleLocalActivity, { passive: true });
    });

    resetTimers();

    return () => {
      ACTIVITY_EVENTS.forEach((eventName) => {
        globalThis.removeEventListener(eventName, handleLocalActivity);
      });
      if (channelRef.current) {
        channelRef.current.close();
        channelRef.current = null;
      }
      clearAllTimers();
    };
  }, [enabled, recordActivity, resetTimers, clearAllTimers]);

  return { isWarning, secondsRemaining, stayActive };
};
